import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useTourStore } from '@/hooks/useTour';
import { useQuery } from '@tanstack/react-query';
import { useGuest } from './GuestContext';

interface TourContextType {
  checkAndShowTourPrompt: () => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const TourProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const { isGuest, hasCompletedTour } = useGuest();
  const { setShowPrompt, initializeTourState, tourDismissed } = useTourStore();

  // Fetch tour status from profile
  const { data: profile } = useQuery({
    queryKey: ['profile-tour-status', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from('profiles')
        .select('tour_dismissed, tour_completed_at, onboarding_completed')
        .eq('id', user.id)
        .single();
      return data;
    },
    enabled: !!user,
  });

  useEffect(() => {
    // Check user-scoped localStorage FIRST (synchronous, survives crashes)
    const visitorKey = user?.id ? `tour_dismissed_${user.id}` : (isGuest ? 'tour_dismissed_guest' : null);
    const tourDismissedLocally = visitorKey ? localStorage.getItem(visitorKey) === 'true' : false;
    
    // If dismissed locally, immediately set state and exit
    if (tourDismissedLocally) {
      initializeTourState(true);
      return;
    }
    
    // For guests: show tour if not completed and not dismissed
    if (isGuest) {
      if (!hasCompletedTour) {
        const timer = setTimeout(() => {
          setShowPrompt(true);
        }, 1000);
        return () => clearTimeout(timer);
      }
      return;
    }

    // For regular users: show based on profile (only if not dismissed locally)
    if (profile) {
      initializeTourState(profile.tour_dismissed || false);
      
      // Show prompt if: user completed onboarding, hasn't dismissed tour, and hasn't completed it
      if (
        profile.onboarding_completed && 
        !profile.tour_dismissed && 
        !profile.tour_completed_at &&
        !tourDismissed
      ) {
        // Small delay to let the home page load
        const timer = setTimeout(() => {
          setShowPrompt(true);
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [profile, initializeTourState, setShowPrompt, tourDismissed, isGuest, hasCompletedTour]);

  const checkAndShowTourPrompt = () => {
    if (!tourDismissed && profile && !profile.tour_dismissed && !profile.tour_completed_at) {
      setShowPrompt(true);
    }
  };

  return (
    <TourContext.Provider value={{ checkAndShowTourPrompt }}>
      {children}
    </TourContext.Provider>
  );
};

export const useTourContext = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTourContext must be used within TourProvider');
  }
  return context;
};
